import React, { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Button, Label, FormFeedback, Input, Row, Col } from 'reactstrap'
import Modals from './Modals'
import server from '../../server'
import chequeImage from '../../assets/images/ChequeCrop.png'
import { Message } from 'primereact/message'

export default function BankInfoModal({
  visible,
  setVisible,
  onSave,
  apiLoginID,
  clientKey,
  pharmacyId,
  userId,
}) {
  const [httpsError, setHttpsError] = useState(false)
  const [paymentError, setPaymentError] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [bankProfile, setBankProfile] = useState(null)
  const [bankProfileLoading, setBankProfileLoading] = useState(false)
  const [pharmacyIdData, setPharmacyIdData] = useState(null)

  useEffect(() => {
    if (pharmacyId) {
      fetchBankProfile(pharmacyId)
    }
  }, [pharmacyId])

  const fetchBankProfile = async (pharmacyId) => {
    setBankProfileLoading(true)
    try {
      const response = await server.get(
        `/payments/pharmacy-bank-profile/${pharmacyId}`
      )
      console.log(response?.data)
      setBankProfile(response?.data)
      setBankProfileLoading(false)
    } catch (error) {
      // 404 is expected when pharmacy has no bank profile yet
      if (error.response?.status !== 404) {
        console.error('Error fetching bank profile:', error)
      }
      setBankProfile(null)
      setBankProfileLoading(false)
    }
  }

  useEffect(() => {
    if (
      window.location.protocol !== 'https:' &&
      window.location.hostname !== 'localhost'
    ) {
      setHttpsError(true)
    }

    // const acceptJsUrl = 'https://jstest.authorize.net/v1/Accept.js'
    const acceptJsUrl="https://js.authorize.net/v1/Accept.js" //!Production

    const script = document.createElement('script')
    script.src = acceptJsUrl
    script.async = true
    document.body.appendChild(script)

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script)
      }
    }
  }, [])

  const validationSchema = Yup.object({
    accountHolderName: Yup.string().required('Account holder name is required'),
    financialInstitutionNumber: Yup.string()
      .required('Financial Institution Number is required')
      .matches(/^[0-9]{3}$/, 'Financial Institution Number must be 3 digits'),
    transitNumber: Yup.string()
      .required('Transit number is required')
      .matches(/^[0-9]{9}$/, 'Transit number must be 9 digits'),
    accountNumber: Yup.string()
      .required('Account number is required')
      .matches(/^[0-9]{4,17}$/, 'Account number must be between 4-17 digits'),
  })

  const formik = useFormik({
    initialValues: {
      accountHolderName: '',
      financialInstitutionNumber: '',
      transitNumber: '',
      accountNumber: '',
    },
    validationSchema,
    onSubmit: (values) => {
      setIsProcessing(true)
      setPaymentError('')

      if (window.Accept) {
        const secureData = {
          authData: {
            clientKey,
            apiLoginID,
          },
          bankData: {
            accountType: 'checking', // or 'savings' - you could add a dropdown for this
            routingNumber: values.transitNumber,
            accountNumber: values.accountNumber,
            nameOnAccount: values.accountHolderName,
            bankName: values.financialInstitutionNumber,
            // Include echeckType if needed (WEB, PPD, etc.)
            echeckType: 'WEB',
          },
        }

        window.Accept.dispatchData(secureData, responseHandler)
      } else {
        setPaymentError('Payment service is unavailable. Accept.js not loaded.')
        setIsProcessing(false)
      }
    },
  })

  const responseHandler = async (response) => {
    setIsProcessing(false)
    console.log('Authorize.Net Accept.js response:', response)

    if (response.messages.resultCode === 'Error') {
      let errorMessage = 'Unknown error occurred'
      if (response.messages.message?.length > 0) {
        errorMessage = response.messages.message[0].text
      }
      setPaymentError(errorMessage)
      return
    }

    const data = {
      ...response.opaqueData,
      userId: userId,
      nameOnAccount: formik.values.accountHolderName,
      bankName: formik.values.financialInstitutionNumber,
    }
    console.log(data)

    try {
      const responseToken = await server.post(
        '/payments/tokenized-bank-profile',
        data
      )

      const token = responseToken.data.data
      const sentData = await server.post(
        '/payments/pharmacy-bank-profile-ids',
        token
      )

      console.log(sentData)

      setPharmacyIdData(sentData?.pharmacyId)

      // Mask account number for security
      const maskedAccountNumber = `****${formik.values.accountNumber.slice(-4)}`

      onSave({
        accountHolderName: formik.values.accountHolderName,
        financialInstitutionNumber: formik.values.financialInstitutionNumber,
        transitNumber: formik.values.transitNumber,
        accountNumber: maskedAccountNumber,
        paymentNonce: response?.opaqueData?.dataValue,
        paymentDescriptor: response?.opaqueData?.dataDescriptor,
        pharmacyId,
      })

      setVisible(false)
      formik.resetForm()
    } catch (error) {
      console.error('Error saving bank information:', error)
      setPaymentError('Failed to save bank information. Please try again.')
    }
  }

  return (
    <Modals
      ModalHeader='Add Bank Information'
      btnName='Save Bank Information'
      visible={visible}
      setVisible={setVisible}
      onButtonClick={() => formik.handleSubmit()}
      disabled={isProcessing}
      loading={isProcessing}
    >
      <div className='p-2'>
        <div className='text-center mb-4'>
          <div className='mb-3'>
            <Message
              severity='info'
              text='Follow this example to find the required information on your void cheque'
              style={{ display: 'inline-block', textAlign: 'left' }}
            />
          </div>
          <div
            style={{
              border: '1px solid #dee2e6',
              padding: '10px',
              borderRadius: '8px',
            }}
          >
            <img
              src={chequeImage}
              alt='Sample cheque showing where to find banking information'
              className='img-fluid'
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </div>
        </div>

        {httpsError && (
          <div className='alert alert-warning mb-3'>
            <strong>Warning:</strong> HTTPS connection is required for bank
            processing. Your current connection is not secure. Testing is still
            available on localhost.
          </div>
        )}

        {bankProfileLoading && (
          <div className='text-center my-3'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
            <p className='mt-2'>Loading bank profile...</p>
          </div>
        )}

        {!bankProfileLoading && bankProfile?.accountNumber && (
          <div className='alert alert-info mb-3'>
            <strong>Existing Bank Account:</strong> {bankProfile.accountNumber}
            <br />
            <strong>Financial Institution Number:</strong>
            {bankProfile.financialInstitutionNumber}
          </div>
        )}

        <form onSubmit={formik.handleSubmit}>
          {paymentError && (
            <div className='alert alert-danger mb-3'>{paymentError}</div>
          )}

          {/* Account Holder Name */}
          <div className='mb-3'>
            <Label for='accountHolderName'>Account Holder Name</Label>
            <Input
              id='accountHolderName'
              name='accountHolderName'
              placeholder='Full name on account'
              value={formik.values.accountHolderName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={
                !!(
                  formik.touched.accountHolderName &&
                  formik.errors.accountHolderName
                )
              }
              disabled={isProcessing}
            />
            <FormFeedback>{formik.errors.accountHolderName}</FormFeedback>
          </div>

          {/* Institution */}
          <div className='mb-3'>
            <Label for='financialInstitutionNumber'>
              Financial Institution Number
            </Label>
            <Input
              id='financialInstitutionNumber'
              name='financialInstitutionNumber'
              placeholder='Financial Institution Number'
              value={formik.values.financialInstitutionNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={
                !!(
                  formik.touched.financialInstitutionNumber &&
                  formik.errors.financialInstitutionNumber
                )
              }
              disabled={isProcessing}
            />
            <FormFeedback>
              {formik.errors.financialInstitutionNumber}
            </FormFeedback>
          </div>

          <Row>
            {/* Transit Number */}
            <Col md={6}>
              <div className='mb-3'>
                <Label for='transitNumber'>Transit #</Label>
                <Input
                  id='transitNumber'
                  name='transitNumber'
                  placeholder='9-digit number'
                  value={formik.values.transitNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    !!(
                      formik.touched.transitNumber &&
                      formik.errors.transitNumber
                    )
                  }
                  disabled={isProcessing}
                />
                <FormFeedback>{formik.errors.transitNumber}</FormFeedback>
              </div>
            </Col>

            {/* Account Number */}
            <Col md={6}>
              <div className='mb-3'>
                <Label for='accountNumber'>Account #</Label>
                <Input
                  id='accountNumber'
                  name='accountNumber'
                  placeholder='Account number'
                  value={formik.values.accountNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    !!(
                      formik.touched.accountNumber &&
                      formik.errors.accountNumber
                    )
                  }
                  disabled={isProcessing}
                />
                <FormFeedback>{formik.errors.accountNumber}</FormFeedback>
              </div>
            </Col>
          </Row>

          <div className='mt-2 small text-muted'>
            Your bank information is securely processed and stored using
            Authorize.Net. We never store your complete bank details on our
            servers.
          </div>
        </form>
      </div>
    </Modals>
  )
}
